import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WeeklyAvailability = () => {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => {
    // Start from current week's Sunday
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - dayOfWeek); // Go back to Sunday
    sunday.setHours(0, 0, 0, 0);
    return sunday;
  });
  
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classTypes, setClassTypes] = useState([]);
  const [selectedClassType, setSelectedClassType] = useState('all');

  // Fetch class types on mount
  useEffect(() => {
    const fetchClassTypes = async () => {
      try {
        const response = await axios.get(`${API}/class-types`);
        // Filter to active class types only
        const activeTypes = response.data.filter(ct => ct.is_active);
        setClassTypes(activeTypes);
      } catch (error) {
        console.error('Failed to fetch class types:', error);
      }
    };
    fetchClassTypes();
  }, []);

  const fetchWeeklyAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = weekStart.toISOString().split('T')[0];
      let url = `${API}/calendar/weekly-availability?start_date=${dateStr}`;
      
      // Add class type filter if not "all"
      if (selectedClassType !== 'all') {
        url += `&class_type_id=${selectedClassType}`;
      }
      
      const response = await axios.get(url);
      setWeeklyData(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly availability:', error);
    } finally {
      setLoading(false);
    }
  }, [weekStart, selectedClassType]);

  useEffect(() => {
    fetchWeeklyAvailability();
  }, [fetchWeeklyAvailability]);

  const goToPreviousWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() - 7);
    setWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + 7);
    setWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - dayOfWeek); // Go back to Sunday
    sunday.setHours(0, 0, 0, 0);
    setWeekStart(sunday);
  };

  // Generate time labels (00:00 to 23:30)
  const timeLabels = [];
  for (let h = 0; h < 24; h++) {
    timeLabels.push(`${h.toString().padStart(2, '0')}:00`);
    timeLabels.push(`${h.toString().padStart(2, '0')}:30`);
  }

  // Get ordered days for the week
  const getOrderedDays = () => {
    if (!weeklyData?.days) return [];
    const days = Object.values(weeklyData.days);
    return days.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const orderedDays = getOrderedDays();

  // Format date for header
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return {
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNum: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' })
    };
  };

  // Check if date is today
  const isToday = (dateStr) => {
    const today = new Date();
    const date = new Date(dateStr + 'T12:00:00');
    return today.toDateString() === date.toDateString();
  };

  // Check if date is today or in the past (should be unavailable)
  const isPastOrToday = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr + 'T12:00:00');
    date.setHours(0, 0, 0, 0);
    return date <= today;
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-slate-900 border-b border-slate-700 px-4 py-3">
        <div className="max-w-full mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-teal-400">Weekly Availability</h1>
            
            {/* Class Type Dropdown */}
            <Select value={selectedClassType} onValueChange={setSelectedClassType}>
              <SelectTrigger className="w-[220px] bg-slate-800 border-slate-600 text-slate-200">
                <SelectValue placeholder="Select class type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 max-h-80">
                <SelectItem value="all" className="text-slate-200">All Class Types</SelectItem>
                {classTypes.map(ct => (
                  <SelectItem key={ct.id} value={ct.id} className="text-slate-200">
                    {ct.name} ({ct.duration_hours}h)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousWeek}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={goToCurrentWeek}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-3"
            >
              Today
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextWeek}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            
            {weeklyData && (
              <span className="ml-4 text-sm text-slate-400">
                {new Date(weeklyData.week_start + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} 
                {' - '}
                {new Date(weeklyData.week_end + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
          
          {/* Legend and Book Now Button */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-emerald-600"></div>
              <span className="text-slate-400">Available</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-red-600"></div>
              <span className="text-slate-400">Not Available</span>
            </div>
            <Button
              onClick={() => navigate('/')}
              className="ml-4 bg-teal-600 hover:bg-teal-500 text-white"
              size="sm"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Book a Class
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[calc(100vh-60px)]">
          <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
        </div>
      ) : (
        <div className="h-[calc(100vh-60px)] overflow-auto">
          <div className="flex min-w-max">
            {/* Time column */}
            <div className="flex-shrink-0 w-16 bg-slate-800/50 sticky left-0 z-10">
              {/* Empty header cell */}
              <div className="h-14 border-b border-slate-700 bg-slate-800"></div>
              {/* Time labels */}
              <div>
                {timeLabels.map((time, idx) => (
                  <div 
                    key={time} 
                    className={`h-6 flex items-center justify-end pr-2 text-xs text-slate-500 border-r border-slate-700 ${idx % 2 === 0 ? 'font-medium text-slate-400' : ''}`}
                  >
                    {idx % 2 === 0 ? time : ''}
                  </div>
                ))}
              </div>
            </div>

            {/* Days grid */}
            {orderedDays.map((day) => {
              const { dayName, dayNum, month } = formatDateHeader(day.date);
              const today = isToday(day.date);
              const pastDay = isPastOrToday(day.date);
              
              return (
                <div key={day.date} className="flex-1 min-w-[120px] border-r border-slate-700 last:border-r-0">
                  {/* Day header */}
                  <div className={`h-14 flex flex-col items-center justify-center border-b border-slate-700 sticky top-0 z-10 ${
                    pastDay ? 'bg-slate-800/50' : today ? 'bg-teal-900/30' : 'bg-slate-800'
                  }`}>
                    <span className={`text-xs uppercase ${pastDay ? 'text-slate-600' : today ? 'text-teal-400' : 'text-slate-500'}`}>{dayName}</span>
                    <span className={`text-lg font-semibold ${pastDay ? 'text-slate-600' : today ? 'text-teal-400' : 'text-slate-200'}`}>{dayNum}</span>
                    <span className={`text-xs ${pastDay ? 'text-slate-600' : today ? 'text-teal-400/70' : 'text-slate-500'}`}>{month}</span>
                  </div>
                  
                  {/* Time blocks */}
                  <div>
                    {day.blocks.map((block, idx) => {
                      // Past days and today are always unavailable
                      const isAvailable = pastDay ? false : block.available;
                      
                      return (
                        <div
                          key={`${day.date}-${block.time}`}
                          className={`h-6 border-b border-slate-700/50 ${
                            isAvailable 
                              ? 'bg-emerald-600/80 hover:bg-emerald-500/80' 
                              : 'bg-red-600/80 hover:bg-red-500/80'
                          } ${idx % 2 === 0 ? 'border-t border-slate-600/30' : ''}`}
                          title={`${day.day_name} ${block.time} - ${isAvailable ? 'Available' : 'Not Available'}`}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyAvailability;
